import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import "./Navbar.css";
import logo from "../assets/logo.jpeg";
import { useAdminAuth } from "../hooks/useAdminAuth";

const NAV_LINKS = [
  { label: "Home", href: "/" },
  { label: "About", href: "/about" },
  { label: "Projects", href: "/projects" },
  { label: "Services", href: "/services" },
  { label: "Careers", href: "/careers" },
];

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAdminMenuOpen, setIsAdminMenuOpen] = useState(false);
  const { isAdmin, adminUser, logout } = useAdminAuth();
  const navigate = useNavigate();
  const adminMenuRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // إغلاق الدروب داون عند الضغط خارجه
  useEffect(() => {
    const onClickOutside = (e) => {
      if (adminMenuRef.current && !adminMenuRef.current.contains(e.target)) {
        setIsAdminMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const handleSignOut = () => {
    logout();
    setIsMenuOpen(false);
    setIsAdminMenuOpen(false);
    navigate("/");
  };

  return (
    <header className={`ihcc-nav ${isScrolled ? "ihcc-nav--scrolled" : ""}`}>
      <div className="ihcc-nav__inner">
        <Link to="/" className="ihcc-nav__brand" aria-label="IHCC — Home">
          <img src={logo} alt="Infinite Horizons Construction Company (IHCC) logo" />
        </Link>

        <nav className="ihcc-nav__links" aria-label="Main navigation">
          {NAV_LINKS.map((link) => (
            <Link key={link.href} to={link.href}>
              {link.label}
            </Link>
          ))}
        </nav>

        <div className="ihcc-nav__actions">
          {isAdmin ? (
            <div className="ihcc-nav__admin-dropdown" ref={adminMenuRef}>
              <button
                type="button"
                className="ihcc-nav__admin-trigger"
                aria-haspopup="true"
                aria-expanded={isAdminMenuOpen}
                onClick={() => setIsAdminMenuOpen((v) => !v)}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="ihcc-nav__admin-trigger-icon"
                >
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
                Admin
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className={`ihcc-nav__admin-trigger-chevron ${isAdminMenuOpen ? "is-open" : ""}`}
                >
                  <path d="m6 9 6 6 6-6" />
                </svg>
              </button>

              <div className={`ihcc-nav__admin-menu ${isAdminMenuOpen ? "is-open" : ""}`}>
                <Link
                  to="/admin"
                  className="ihcc-nav__admin-menu-item"
                  onClick={() => setIsAdminMenuOpen(false)}
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M12 2a10 10 0 1 0 10 10 10 10 0 0 0-10-10z" />
                    <path d="M12 12a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z" />
                    <path d="M12 14a4 4 0 0 0-4 4h8a4 4 0 0 0-4-4z" />
                  </svg>
                  Jobs
                </Link>

                <Link
                  to="/admin/applicants"
                  className="ihcc-nav__admin-menu-item"
                  onClick={() => setIsAdminMenuOpen(false)}
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                  </svg>
                  Applicants
                </Link>

                <button
                  type="button"
                  className="ihcc-nav__admin-menu-item ihcc-nav__admin-menu-item--danger"
                  onClick={handleSignOut}
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                    <path d="M16 17l5-5-5-5" />
                    <path d="M21 12H9" />
                  </svg>
                  Sign Out
                </button>
              </div>
            </div>
          ) : (
            <Link to="/contact" className="ihcc-nav__cta">
              Contact Us
            </Link>
          )}

          <button
            className={`ihcc-nav__burger ${isMenuOpen ? "is-open" : ""}`}
            aria-label="Open menu"
            aria-expanded={isMenuOpen}
            onClick={() => setIsMenuOpen((v) => !v)}
          >
            <span />
            <span />
            <span />
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <div className={`ihcc-nav__mobile ${isMenuOpen ? "is-open" : ""}`}>
        {NAV_LINKS.map((link) => (
          <Link key={link.href} to={link.href} onClick={() => setIsMenuOpen(false)}>
            {link.label}
          </Link>
        ))}

        {isAdmin ? (
          <div className="ihcc-nav__admin ihcc-nav__admin--mobile">
            <Link 
              to="/admin" 
              className="ihcc-nav__admin-link ihcc-nav__admin-link--mobile"
              onClick={() => setIsMenuOpen(false)}
            >
              <svg 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
                style={{ width: '18px', height: '18px', marginRight: '6px' }}
              >
                <path d="M12 2a10 10 0 1 0 10 10 10 10 0 0 0-10-10z" />
                <path d="M12 12a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z" />
                <path d="M12 14a4 4 0 0 0-4 4h8a4 4 0 0 0-4-4z" />
              </svg>
              Manage Jobs
            </Link>

            <Link 
              to="/admin/applicants" 
              className="ihcc-nav__admin-link ihcc-nav__admin-link--mobile"
              onClick={() => setIsMenuOpen(false)}
            >
              <svg 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
                style={{ width: '18px', height: '18px', marginRight: '6px' }}
              >
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
              View Applicants
            </Link>
            
            <button
              type="button"
              className="ihcc-nav__signout ihcc-nav__signout--mobile"
              onClick={handleSignOut}
            >
              Sign Out
            </button>
          </div>
        ) : (
          <Link
            to="/contact"
            className="ihcc-nav__cta ihcc-nav__cta--mobile"
            onClick={() => setIsMenuOpen(false)}
          >
            Contact Us
          </Link>
        )}
      </div>
    </header>
  );
}